/**
 * Where a record lives in the app. Notifications and audit entries carry `entityType` + `entityId`;
 * this picks the first candidate screen that is actually registered, so links appear as soon as the
 * milestone that owns the screen lands (and never point at a missing page).
 */

const CANDIDATES: Record<string, string[]> = {
  sales_invoice: ['sales/invoices/:id'],
  sales_order: ['sales/orders/:id'],
  delivery: ['sales/deliveries/:id', 'deliveries/:id'],
  quotation: ['quotations/:id'],
  purchase_order: ['purchases/orders/:id', 'purchases/:id'],
  goods_receipt: ['purchases/receipts/:id', 'goods-receipts/:id'],
  supplier_invoice: ['purchases/bills/:id', 'bills/:id'],
  payment: ['payments/:id'],
  contact: ['customers/:id', 'suppliers/:id', 'contacts/:id'],
  customer: ['customers/:id', 'contacts/:id'],
  supplier: ['suppliers/:id', 'contacts/:id'],
  product: ['products/:id'],
  product_variant: ['inventory', 'products'],
  variant: ['inventory', 'products'],
  sales_return: ['returns/sales/:id', 'returns/:id'],
  purchase_return: ['returns/purchases/:id', 'returns/:id'],
  credit_note: ['returns/credit-notes/:id', 'returns/:id'],
  debit_note: ['returns/debit-notes/:id', 'returns/:id'],
  stock_adjustment: ['inventory/adjustments/:id'],
  stock_count: ['inventory/counts/:id'],
  journal_entry: ['accounting/journal/:id'],
  account: ['accounting/accounts/:id'],
  reminder_rule: ['reminders'],
  role: ['staff'],
  user: ['staff'],
  device: ['sync'],
  sync_conflict: ['sync'],
};

/** A link for a record, or null when no registered screen shows it. */
export function entityLink(
  entityType: string | null | undefined,
  entityId: string | null | undefined,
  registeredPaths: Iterable<string>,
): string | null {
  if (!entityType) return null;
  const candidates = CANDIDATES[entityType.toLowerCase()];
  if (!candidates) return null;
  const registered = new Set([...registeredPaths].map((p) => p.replace(/^\/+/, '')));
  for (const template of candidates) {
    if (!registered.has(template)) continue;
    if (template.includes(':id')) {
      if (!entityId) continue;
      return `/${template.replace(':id', encodeURIComponent(entityId))}`;
    }
    return `/${template}`;
  }
  return null;
}

/** "sales_invoice" → "Sales invoice". */
export function entityLabel(entityType: string | null | undefined): string {
  if (!entityType) return '';
  const s = entityType.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}
